import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Volunteer } from '../../types';
import { liabilityWaiverApi } from '../../services/api';
import { formatUTCDateToLocal, isPastTime } from '../../utils/timezone';
import './LiabilityWaiversPage.css';

const LiabilityWaiversPage: React.FC = () => {
  const navigate = useNavigate();
  const [allWaivers, setAllWaivers] = useState<Volunteer[]>([]);
  const [expiredWaivers, setExpiredWaivers] = useState<Volunteer[]>([]);
  const [filteredWaivers, setFilteredWaivers] = useState<Volunteer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'current' | 'expired' | 'expiring'>('all');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchWaivers();
  }, []);

  useEffect(() => {
    filterWaivers();
  }, [allWaivers, expiredWaivers, searchTerm, filterStatus]);

  const fetchWaivers = async () => {
    setLoading(true);
    setMessage('');
    try {
      const [allData, expiredData] = await Promise.all([
        liabilityWaiverApi.getAll(),
        liabilityWaiverApi.getExpired()
      ]);
      
      setAllWaivers(allData);
      setExpiredWaivers(expiredData);
    } catch (err) {
      setMessage('Failed to fetch liability waivers');
    } finally {
      setLoading(false);
    }
  };

  const filterWaivers = () => {
    let filtered = allWaivers;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(volunteer =>
        volunteer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.passcode.includes(searchTerm)
      );
    }

    // Filter by status
    if (filterStatus === 'expired') {
      filtered = expiredWaivers;
    } else if (filterStatus === 'current') {
      filtered = filtered.filter(volunteer => 
        volunteer.liabilityWaiverAccepted && 
        !isExpired(volunteer.liabilityWaiverExpiresAt) &&
        !isExpiringSoon(volunteer.liabilityWaiverExpiresAt)
      );
    } else if (filterStatus === 'expiring') {
      filtered = filtered.filter(volunteer => 
        volunteer.liabilityWaiverAccepted && 
        isExpiringSoon(volunteer.liabilityWaiverExpiresAt)
      );
    }

    setFilteredWaivers(filtered);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format as MM/DD/YYYY
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return isPastTime(expiresAt);
  };

  const isExpiringSoon = (expiresAt?: string) => {
    if (!expiresAt) return false;
    const expirationDate = new Date(expiresAt);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expirationDate <= thirtyDaysFromNow && expirationDate > new Date();
  };

  const getStatusBadge = (volunteer: Volunteer) => {
    if (!volunteer.liabilityWaiverAccepted) {
      return <span className="status-badge not-accepted">Not Accepted</span>;
    }
    
    if (isExpired(volunteer.liabilityWaiverExpiresAt)) {
      return <span className="status-badge expired">Expired</span>;
    }
    
    if (isExpiringSoon(volunteer.liabilityWaiverExpiresAt)) {
      return <span className="status-badge expiring-soon">Expires Soon</span>;
    }
    
    return <span className="status-badge current">Current</span>;
  };

  const handleBack = () => {
    navigate('/admin-panel');
  };

  return (
    <div className="liability-waivers-container">
      <div className="liability-waivers-card">
        <div className="liability-waivers-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="liability-waivers-title">Liability Waivers</h1>
          <p className="liability-waivers-subtitle">
            View and manage liability waiver status, track expiration dates, and identify expired waivers
          </p>
        </div>

        <div className="liability-waivers-content">
          {/* Stats */}
          <div className="stats-section">
            <div className="stat-card">
              <div className="stat-number">{allWaivers.length}</div>
              <div className="stat-label">Total Waivers</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{expiredWaivers.length}</div>
              <div className="stat-label">Expired</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {allWaivers.filter(v => isExpiringSoon(v.liabilityWaiverExpiresAt)).length}
              </div>
              <div className="stat-label">Expiring Soon</div>
            </div>
          </div>

          {/* Controls */}
          <div className="waiver-controls">
            <div className="search-filter-section">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search waivers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('all')}
                >
                  All
                </button>
                <button
                  className={`filter-btn ${filterStatus === 'current' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('current')}
                >
                  Current
                </button>
                <button
                  className={`filter-btn ${filterStatus === 'expiring' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('expiring')}
                >
                  Expiring Soon
                </button>
                <button
                  className={`filter-btn ${filterStatus === 'expired' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('expired')}
                >
                  Expired
                </button>
              </div>
            </div>
            <div className="action-buttons">
              <button onClick={fetchWaivers} className="refresh-btn">
                Refresh Data
              </button>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          {/* Waivers List */}
          <div className="waivers-list">
            <h3>Liability Waivers ({filteredWaivers.length})</h3>
            {loading ? (
              <div className="loading">Loading liability waivers...</div>
            ) : filteredWaivers.length === 0 ? (
              <div className="no-waivers">No waivers found</div>
            ) : (
              <div className="waivers-table">
                <div className="waivers-table-inner">
                  <div className="table-header">
                    <div className="col-name">Name</div>
                    <div className="col-email">Email</div>
                    <div className="col-phone">Phone</div>
                    <div className="col-passcode">User code</div>
                    <div className="col-accepted">Accepted</div>
                    <div className="col-expires">Expires</div>
                    <div className="col-status">Status</div>
                    <div className="col-active">Active</div>
                  </div>
                  <div className="table-rows-container">
                    {filteredWaivers.map((volunteer) => (
                      <div key={volunteer.id} className={`table-row ${isExpired(volunteer.liabilityWaiverExpiresAt) ? 'expired-row' : ''}`}>
                        <div className="col-name">
                          {volunteer.firstName} {volunteer.lastName}
                        </div>
                        <div className="col-email">{volunteer.email}</div>
                        <div className="col-phone">{volunteer.phoneNumber}</div>
                        <div className="col-passcode">{volunteer.passcode}</div>
                        <div className="col-accepted">{formatDate(volunteer.liabilityWaiverAcceptedAt)}</div>
                        <div className={`col-expires ${isExpired(volunteer.liabilityWaiverExpiresAt) ? 'expired-date' : ''}`}>
                          {formatDate(volunteer.liabilityWaiverExpiresAt)}
                        </div>
                        <div className="col-status">{getStatusBadge(volunteer)}</div>
                        <div className="col-active">
                          <span className={`status-badge ${volunteer.isActive ? 'active' : 'inactive'}`}>
                            {volunteer.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="liability-waivers-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiabilityWaiversPage;
